import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  keywords?: string;
  noIndex?: boolean;
  structuredData?: object;
  breadcrumbs?: Array<{ name: string; item: string }>;
}

// Detect if we're on staging/non-production environment
// Robust check: VITE_APP_ENV='production' AND hostname is 'sporto.id'
// If either is false, we treat as non-production (noindex)
const isProduction =
  (import.meta.env.VITE_APP_ENV as string) === 'production' &&
  (typeof window !== 'undefined' ? window.location.hostname === 'sporto.id' : true);

const SEO: React.FC<SEOProps> = ({
  title = 'Sporto - Platform Booking Arena Olahraga Terpraktis di Indonesia',
  description = 'Platform booking arena olahraga terpraktis di Indonesia. Temukan dan pesan arena padel, futsal, badminton, tenis, dan olahraga lainnya dengan mudah, cepat, dan aman!',
  image = 'https://cdn.sporto.id/sporto-og.png',
  url = 'https://sporto.id',
  type = 'website',
  keywords = 'booking arena, arena padel, arena futsal, arena badminton, arena tenis, olahraga indonesia, sporto',
  noIndex,
  structuredData,
  breadcrumbs,
}) => {
  // Default noIndex based on environment if not explicitly set to false
  // If noIndex prop is provided, strictly respect it.
  // If noIndex prop is undefined, default to !isProduction.
  const shouldNoIndex = noIndex !== undefined ? noIndex : !isProduction;

  const siteUrl = 'https://sporto.id';
  const fullUrl = url.startsWith('http') ? url : `${siteUrl}${url}`;
  const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;

  // JSON-LD Schemas List
  const schemas = [];

  // 1. Organization Schema (Always present)
  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Sporto',
    url: siteUrl,
  });

  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Sporto',
    url: siteUrl,
    logo: `${siteUrl}/sporto-logo.png`,
    description: 'Platform booking arena olahraga terpraktis di Indonesia',
    sameAs: [
      'https://www.instagram.com/sporto.id',
      // Add other social media URLs
    ],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'ID',
    },
  });

  // 2. Custom Structured Data (if provided)
  if (structuredData) {
    schemas.push(structuredData);
  }

  // 3. BreadcrumbList Schema (if breadcrumbs provided)
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.item.startsWith('http') ? crumb.item : `${siteUrl}${crumb.item}`,
      })),
    });
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      {/* Robots meta tag */}
      <meta name="robots" content={shouldNoIndex ? 'noindex, nofollow' : 'index, follow'} />
      {shouldNoIndex && <meta name="googlebot" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:secure_url" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="360" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Sporto" />
      <meta property="og:locale" content="id_ID" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />

      {/* Additional Meta Tags */}
      <meta name="author" content="Sporto" />
      <meta name="theme-color" content="#f97316" />

      {/* Structured Data Scripts */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
